/**
 * Collision detection for the Product Map's single shared `DndContext`.
 *
 * Bare `closestCorners`/`closestCenter` run against EVERY registered
 * droppable at once, and this page registers three different kinds on top of
 * each other:
 *  - capability sortables (`CapabilityRow`, `useSortable({ id: capability.id })`)
 *  - area container droppables (`AreaRow`, `useDroppable({ id: areaContainerId(...) })`
 *    on the capability-list body)
 *  - area sortables (`AreaRow`, `useSortable({ id: area.id, data: { type: 'area' } })`,
 *    whose `setNodeRef` sits on the OUTERMOST wrapper of the whole area
 *    block — header + every capability row inside it)
 *
 * An area's sortable rect therefore encloses its own capability rows. Under
 * a single flat algorithm run over everything at once, that giant rect's
 * nearest corner/center frequently beats a sibling capability row — worst
 * for the FIRST row in an area, which sits closest to the area rect's top
 * corner. When `over` resolves to an area id, `resolveColumnId` in
 * `useProductMapDragDrop` can't map it to a column (`parseContainerAreaId`
 * returns `undefined`, and no capability has that id), so both
 * `handleDragOver`'s splice and `handleDragEnd`'s mutation silently bail —
 * reproducing both "can't drag onto the first slot" and "no predictive
 * re-splice" symptoms from the same root cause.
 *
 * This collision function fixes that AND makes the live preview track the
 * drag predictively (rows slide open a gap as the cursor approaches, instead
 * of snapping only once the cursor fully crosses into the next row) via a
 * two-stage resolution, run separately per drag type:
 *
 *  1. Filter `droppableContainers` by the active drag's type BEFORE running
 *     any algorithm — an area drag only ever considers area droppables; a
 *     capability drag never considers area droppables (same reasoning as
 *     above: an area's enclosing rect must never win against its own rows).
 *
 *  2. Area drags: `closestCenter` over the filtered area droppables. Areas
 *     are a plain vertical list in a single `SortableContext`, so there's
 *     no "which group" question — `closestCenter`'s midpoint-based switching
 *     is simply the more predictive choice than `closestCorners` here too.
 *
 *  3. Capability drags: resolved in two stages, because a capability move
 *     has both a "which group" (pointer question) and a "which slot within
 *     that group" (predictive-preview question) — a single algorithm can't
 *     answer both well. `pointerWithin`/`closestCenter` over the raw pointer
 *     coordinate only changes `over` once the cursor fully crosses into the
 *     next row's rect, which is what made the preview feel late and abrupt;
 *     `closestCenter` restricted to one container's rows switches at the
 *     midpoint between two row centers, so neighbours start sliding as the
 *     dragged row approaches — the "floats nicely to it" feel.
 *     a. Resolve the target CONTAINER: `pointerWithin` over container
 *        droppables only. Falls back to `closestCenter` over containers only
 *        when the pointer isn't within any (cursor over an area header, or
 *        outside the list). Falls back further to `closestCenter` over ALL
 *        non-area droppables if there are no containers at all.
 *     b. Resolve the precise SLOT: `closestCenter` over just the capability
 *        droppables whose `data.current.containerId` matches the resolved
 *        container's id (see `CapabilityRow`'s `useSortable` data) — this is
 *        what keeps a capability drag from ever resolving to a row in a
 *        sibling container even when that row's center is nearer in raw
 *        screen space.
 *     c. Empty container: if it has no capability droppables, return the
 *        container collision itself — `useProductMapDragDrop` already
 *        special-cases a container `over.id` as an append-at-end drop.
 *
 * The active item is deliberately NOT excluded from the closestCenter
 * candidate set in step 3b — `over === active` is the natural "stay here"
 * state and provides useful hysteresis right around the drag's origin slot.
 *
 * @module features/product-map/productMapCollision
 */

import { closestCenter, pointerWithin, type CollisionDetection, type DroppableContainer } from '@dnd-kit/core';
import type { ProductMapDragType } from './hooks/useProductMapDragDrop';

function droppableType(container: DroppableContainer): ProductMapDragType | 'container' | undefined {
  return container.data.current?.type as ProductMapDragType | 'container' | undefined;
}

function containerIdOf(container: DroppableContainer): string | undefined {
  return container.data.current?.containerId as string | undefined;
}

export const productMapCollisionDetection: CollisionDetection = (args) => {
  const activeType = args.active.data.current?.type as ProductMapDragType | undefined;

  if (activeType === 'area') {
    // Area reorder: only ever consider other area sortables — never a
    // capability row or an area's own container droppable.
    const areaDroppables = args.droppableContainers.filter((c) => droppableType(c) === 'area');
    return closestCenter({ ...args, droppableContainers: areaDroppables });
  }

  // Capability drag (including the case where `active.data.current` is
  // missing type info — default to the capability-safe filter rather than
  // ever letting an area rect win). Never consider area droppables: that's
  // what let the giant area rect steal collisions from the first row.
  const candidateDroppables = args.droppableContainers.filter((c) => droppableType(c) !== 'area');
  const containerDroppables = candidateDroppables.filter((c) => droppableType(c) === 'container');

  // Stage 1: resolve the target container from the pointer, falling back to
  // closestCenter over containers, then over everything non-area.
  let targetContainer: DroppableContainer | undefined;
  if (containerDroppables.length > 0) {
    const pointerHits = pointerWithin({ ...args, droppableContainers: containerDroppables });
    if (pointerHits.length > 0) {
      targetContainer = containerDroppables.find((c) => c.id === pointerHits[0]!.id);
    } else {
      const centerHits = closestCenter({ ...args, droppableContainers: containerDroppables });
      targetContainer = containerDroppables.find((c) => c.id === centerHits[0]?.id);
    }
  }

  if (!targetContainer) {
    return closestCenter({ ...args, droppableContainers: candidateDroppables });
  }

  // Stage 2: resolve the precise slot within the target container.
  const containerId = String(targetContainer.id);
  const slotDroppables = candidateDroppables.filter(
    (c) => droppableType(c) === 'capability' && containerIdOf(c) === containerId
  );

  if (slotDroppables.length === 0) {
    // Empty container — the append/drop-into-empty-area path.
    return [{ id: targetContainer.id }];
  }

  return closestCenter({ ...args, droppableContainers: slotDroppables });
};
